import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useApi } from '../auth/session.tsx';

export const keys = {
  ws: (ws: string) => ['ws', ws] as const,
  info: (ws: string) => ['ws', ws, 'info'] as const,
  status: (ws: string) => ['ws', ws, 'status'] as const,
  folders: (ws: string) => ['ws', ws, 'folders'] as const,
  items: (ws: string, params: Record<string, unknown>) => ['ws', ws, 'items', params] as const,
  item: (ws: string, id: string) => ['ws', ws, 'item', id] as const,
  note: (ws: string, id: string) => ['ws', ws, 'note', id] as const,
  text: (ws: string, id: string) => ['ws', ws, 'text', id] as const,
  related: (ws: string, id: string, direction: string) => ['ws', ws, 'related', id, direction] as const,
  search: (ws: string, q: string) => ['ws', ws, 'search', q] as const,
  activity: (ws: string) => ['ws', ws, 'activity'] as const,
  connections: (ws: string) => ['ws', ws, 'connections'] as const,
  agentSetup: (ws: string) => ['ws', ws, 'agent-setup'] as const,
  members: (ws: string) => ['ws', ws, 'members'] as const,
  invitations: (ws: string) => ['ws', ws, 'invitations'] as const,
  shares: (ws: string, itemId: string) => ['ws', ws, 'shares', itemId] as const,
  revisions: (ws: string, itemId: string) => ['ws', ws, 'revisions', itemId] as const,
  annotations: (ws: string, itemId: string) => ['ws', ws, 'annotations', itemId] as const,
  /**
   * A Site version's preview link. Kept outside the workspace key on purpose:
   * other changes in the workspace must not reload a Site someone is using.
   */
  sitePreview: (ws: string, itemId: string, revisionId: string) => ['site-preview', ws, itemId, revisionId] as const,
  preferences: (ws: string) => ['ws', ws, 'preferences'] as const,
  voices: (ws: string) => ['ws', ws, 'voices'] as const,
};

export function useWorkspaceId(): string {
  const { workspaceId } = useParams();
  if (!workspaceId) throw new Error('No workspace in route');
  return workspaceId;
}

export function useMe() {
  const api = useApi();
  return useQuery({ queryKey: ['me'], queryFn: () => api.me() });
}

export function useWorkspaceInfo(ws: string) {
  const api = useApi();
  return useQuery({ queryKey: keys.info(ws), queryFn: () => api.workspace(ws) });
}

export function useFolders(ws: string) {
  const api = useApi();
  return useQuery({ queryKey: keys.folders(ws), queryFn: () => api.folders(ws) });
}

/** The signed-in person's own settings (the same in every workspace). */
export function usePreferences(ws: string) {
  const api = useApi();
  return useQuery({ queryKey: keys.preferences(ws), queryFn: () => api.preferences(ws), staleTime: 60_000 });
}

export function useItem(ws: string, itemId: string) {
  const api = useApi();
  return useQuery({ queryKey: keys.item(ws, itemId), queryFn: () => api.item(ws, itemId) });
}

/** Invalidates everything for the workspace after a mutation (the change stream does the same for others' changes). */
export function useWorkspaceMutation<A, R>(ws: string, fn: (args: A) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => client.invalidateQueries({ queryKey: keys.ws(ws) }) });
}

/**
 * Subscribes to committed-change hints and refetches the workspace's queries
 * (throttled). Returns whether the stream is currently connected.
 */
export function useLiveWorkspace(ws: string): boolean {
  const api = useApi();
  const client = useQueryClient();
  const [connected, setConnected] = useState(true);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastSeq = -1;
    const stop = api.subscribe(
      ws,
      (seq) => {
        if (seq === lastSeq) return;
        lastSeq = seq;
        clearTimeout(timer);
        timer = setTimeout(() => void client.invalidateQueries({ queryKey: keys.ws(ws) }), 250);
      },
      setConnected,
    );
    return () => {
      clearTimeout(timer);
      stop();
    };
  }, [api, client, ws]);
  return connected;
}
